import { useEffect, useMemo, useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { MarkdownBody } from './MarkdownBody';
import { getAuthHeaders } from '@/lib/trpc';
import { SERVER_URL } from '@/lib/env';
import { downloadBlob } from '@/lib/download-file';
import { isStorageObjectUrl, withInlineStorageUrl } from '@/components/compliance/StorageTextFilePreview';
import { EvidencePdfDownload } from './EvidencePdfDownload';
import { FileText, Loader2, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@deepguide-ai/ui/button';
import { ScrollArea } from '@deepguide-ai/ui/scroll-area';

interface MarkdownPreviewProps {
  url: string;
  filename: string;
  evidenceFileId?: string;
  /**
   * Optional: when a rendered markdown link points at a storage object
   * (`/api/storage/object/…`) — e.g. an evidence-file link inside a procedure
   * run-summary — call this instead of opening the raw URL, so the caller can
   * swap the preview to that file (in-app preview modal). Falls back to a normal
   * link when not provided.
   */
  onEvidenceFileClick?: (href: string) => void;
}

export function MarkdownPreview({
  url,
  filename,
  evidenceFileId,
  onEvidenceFileClick,
}: MarkdownPreviewProps) {
  const { workspaceId, auditCycleId } = useParams({ strict: false });
  const [content, setContent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [originalBlob, setOriginalBlob] = useState<Blob | null>(null);

  const authenticatedStorage = useMemo(() => {
    try {
      const sourceUrl = new URL(url, window.location.origin);
      return (isStorageObjectUrl(url) || sourceUrl.pathname.startsWith('/api/storage/auditor-vendor/'))
        && (sourceUrl.origin === new URL(SERVER_URL).origin || sourceUrl.origin === window.location.origin);
    } catch {
      return false;
    }
  }, [url]);

  const handleCopy = async () => {
    if (content === null) return;
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Failed to copy');
    }
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
    setContent(null);
    setOriginalBlob(null);
    const controller = new AbortController();
    (async () => {
      try {
        const headers = authenticatedStorage ? await getAuthHeaders() : undefined;
        const response = await fetch(authenticatedStorage ? withInlineStorageUrl(url) : url, {
          credentials: authenticatedStorage ? 'include' : 'omit', headers, signal: controller.signal,
        });
        if (!response.ok) throw new Error(`Failed to load file (${response.status})`);
        const blob = authenticatedStorage ? await response.blob() : null;
        const text = blob ? await blob.text() : await response.text();
        if (!controller.signal.aborted) { setContent(text); setOriginalBlob(blob); }
      } catch (error) {
        if (!controller.signal.aborted) setError(error instanceof Error ? error.message : 'Failed to load preview');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [url, authenticatedStorage]);

  if (loading) {
    return (
      <div className="flex h-full w-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading {filename}…
      </div>
    );
  }

  if (error || content === null) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-4 rounded-md bg-muted/20 p-8">
        <FileText className="h-8 w-8 text-muted-foreground" />
        <span className="text-sm text-status-red">{error ?? 'Failed to load preview'}</span>
        {evidenceFileId ? (
          <EvidencePdfDownload key={evidenceFileId} evidenceFileId={evidenceFileId} url={url} label="Download" />
        ) : <Button variant="outline" size="sm" asChild>
          <a href={url} download={filename}>
            Download
          </a>
        </Button>}
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col overflow-hidden rounded-md border bg-card">
      <div className="flex h-[50px] shrink-0 items-center justify-between border-b border-border bg-surface-subtle px-4">
        <span className="min-w-0 flex-1 truncate text-xs font-medium text-text-secondary">
          {filename}
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 shrink-0 text-[10px]"
          onClick={handleCopy}
        >
          {copied ? (
            <Check className="mr-1 h-3.5 w-3.5" />
          ) : (
            <Copy className="mr-1 h-3.5 w-3.5" />
          )}
          {copied ? 'Copied' : 'Copy'}
        </Button>
        {evidenceFileId ? (
          <EvidencePdfDownload key={evidenceFileId} evidenceFileId={evidenceFileId} url={url} label="Download" />
        ) : authenticatedStorage ? <Button variant="ghost" size="sm" className="h-8 shrink-0 text-[10px]"
          disabled={!originalBlob} onClick={() => { if (originalBlob) downloadBlob(originalBlob, filename) }}>
          Download
        </Button> : <Button variant="ghost" size="sm" className="h-8 shrink-0 text-[10px]" asChild>
          <a href={url} download={filename}>Download</a>
        </Button>}
      </div>
      <ScrollArea
        className="min-h-0 flex-1"
        viewClassName="[&>div]:!block"
        type="hover"
      >
        <div className="px-6 py-5 font-sans text-[14px] leading-6 text-text-secondary">
        <MarkdownBody content={content} onEvidenceFileClick={onEvidenceFileClick} policyLinkContext={{ workspaceId, auditCycleId }} />
        </div>
      </ScrollArea>
    </div>
  );
}
